import { useState } from 'react'
import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Briefcase, IdCard, Trophy, Search, ExternalLink } from 'lucide-react'
import {
  useLatestJobs,
  useAdmitCards,
  useExamResults,
  type SarkariListing,
} from '@/hooks/use-supabase-data'

type TabKey = 'jobs' | 'admit' | 'results'

const TABS: { key: TabKey; label: string; icon: typeof Briefcase; source: string }[] = [
  { key: 'jobs', label: 'Latest Jobs', icon: Briefcase, source: 'https://www.sarkariresult.com/latestjob/' },
  { key: 'admit', label: 'Admit Cards', icon: IdCard, source: 'https://www.sarkariresult.com/admitcard/' },
  { key: 'results', label: 'Results', icon: Trophy, source: 'https://www.sarkariresult.com/results/' },
]

/** Each table stores its link under a different column. */
function linkOf(item: SarkariListing): string | null {
  return item.apply_url || item.admit_card_url || item.result_url || null
}

export default function JobsExams() {
  const [tab, setTab] = useState<TabKey>('jobs')
  const [query, setQuery] = useState('')

  const jobs = useLatestJobs()
  const admit = useAdmitCards()
  const results = useExamResults()

  const active = tab === 'jobs' ? jobs : tab === 'admit' ? admit : results
  const activeTab = TABS.find((t) => t.key === tab)!

  const q = query.trim().toLowerCase()
  const filtered = q
    ? active.items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          (i.organization || '').toLowerCase().includes(q),
      )
    : active.items

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2">
            Jobs &amp; Exams
          </h1>
          <p className="font-inter text-muted-foreground text-lg">
            Sarkari naukri, admit cards and results — updated daily
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {TABS.map(({ key, label, icon: Icon }) => {
            const counts = key === 'jobs' ? jobs : key === 'admit' ? admit : results
            return (
              <button
                key={key}
                onClick={() => { setTab(key); setQuery('') }}
                data-testid={`tab-${key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-inter text-sm transition-all ${
                  tab === key
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-card text-foreground hover:bg-primary/5 border'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
                {!counts.loading && (
                  <span className="text-xs opacity-75">({counts.items.length})</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Search + source link */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by post or department..."
              className="pl-9"
              data-testid="input-search-jobs"
            />
          </div>
          <Button variant="outline" asChild>
            <a href={activeTab.source} target="_blank" rel="noopener noreferrer">
              See all <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </Button>
        </div>

        {active.loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : active.error ? (
          <p className="text-center text-destructive py-16">{active.error}</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-16">
            {q ? `Nothing matches "${query}"` : 'Nothing here yet — check back soon.'}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => {
                const href = linkOf(item)
                return (
                  <Card key={item.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex flex-col h-full">
                      <h3 className="font-inter font-semibold text-foreground mb-2 leading-snug">
                        {item.title}
                      </h3>
                      {item.organization && (
                        <Badge variant="secondary" className="w-fit mb-3">
                          {item.organization}
                        </Badge>
                      )}
                      {href && (
                        <Button size="sm" className="mt-auto w-full" asChild>
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {tab === 'jobs' ? 'Apply' : tab === 'admit' ? 'Download' : 'View Result'}
                            <ExternalLink className="w-3.5 h-3.5 ml-1" />
                          </a>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  )
}
